import React, { useState, useEffect } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Form, Button } from 'semantic-ui-react'
import FWOIteractiveTemplateRow from '../helpers/FWOIteractiveTemplateRow'
import {
  getInitialValues,
  getAddressSchema,
  getPageFields,
  getFieldSchema,
  buildAddressFromValues,
} from '../common'
import {
  FormKey,
  IInteractiveTemplateField,
  IInteractiveTemplatePage,
  IInteractiveTemplateSection,
  InteractiveTemplateFieldType,
} from '../../../models/InteractiveTemplate'
import Conditional from '../../common/Conditional'
import Accordion from '../../common/Accordion'
import PageTitle from '../../PageTitle'

enum Page {
  EmploymentDetails = 'Employment details',
  LeaveDetails = 'Leave details',
}

export const AnnualLeaveAgreementTemplate = (props) => {
  const [initialValues, setInitialValues] = useState({})
  const [validationSchema, setValidationSchema] = useState(null)
  const [showEmploymentDetailsPage, setShowEmploymentDetailsPage] =
    useState(false)
  const [showLeaveDetailsPage, setShowLeaveDetailsPage] = useState(false)
  const [currentPage, setCurrentPage] = useState(null)
  const [validEmploymentDetails, setValidEmploymentDetails] = useState(false)
  const [employeeU18, setEmployeeU18] = useState('')
  const [tacEmployment, setTacEmployment] = useState('')
  const [tacU18, setTacU18] = useState('')

  useEffect(() => {
    const values = getInitialValues(props.template)
    setInitialValues(values)
  }, [])

  useEffect(() => {
    setCurrentPage(props.show ? Page.EmploymentDetails : null)
  }, [props.show])

  useEffect(() => {
    const schema = getPageSchema(currentPage)
    setValidationSchema(schema)
    if (currentPage === Page.EmploymentDetails) {
      setShowEmploymentDetailsPage(true)
      setShowLeaveDetailsPage(false)
    } else if (currentPage === Page.LeaveDetails) {
      setShowEmploymentDetailsPage(false)
      setShowLeaveDetailsPage(true)
    }
  }, [currentPage])

  const getPageSchema = (currentPage) => {
    const page = props.template?.pages?.find(
      (page) => page.name === currentPage
    )

    if (!page) {
      return
    }

    const fields = getPageFields(page)

    const schema = fields.reduce((schema, field) => {
      if (field.type === InteractiveTemplateFieldType.Instruction) {
        return schema
      }
      if (field.type === InteractiveTemplateFieldType.Hidden) {
        // skip validation for hidden field
        return schema
      }
      if (field.type === InteractiveTemplateFieldType.Address) {
        return getAddressSchema(schema, field)
      }

      if (
        field.replacement === 'GUARDIAN_FIRSTNAME' ||
        field.replacement === 'GUARDIAN_LASTNAME' ||
        field.replacement === 'TAC_U18'
      ) {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('EMPLOYEE_U18', {
            is: (WHY_DIRECTION) => WHY_DIRECTION === 'Yes',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (field.replacement === 'TAC_EMPLOYMENT') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('EMPLOYEE_U18', {
            is: (WHY_DIRECTION) => WHY_DIRECTION === 'No',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (field.replacement === 'AWARD_CONDITION') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('TAC_EMPLOYMENT', {
            is: (TAC_EMPLOYMENT) =>
              TAC_EMPLOYMENT === 'Award' ||
              TAC_EMPLOYMENT === 'Enterprise Agreement',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (field.replacement === 'AWARD_U18') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('TAC_U18', {
            is: (TAC_U18) =>
              TAC_U18 === 'Award' || TAC_U18 === 'Enterprise Agreement',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      return getFieldSchema(schema, field)
    }, {})

    return Yup.object().shape(schema)
  }

  const renderField = (field: IInteractiveTemplateField, index: number) => {
    if (field.replacement === 'EMPLOYEE_U18') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setEmployeeU18(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (field.replacement === 'TAC_EMPLOYMENT') {
      if (employeeU18 === 'no') {
        return (
          <FWOIteractiveTemplateRow
            key={index}
            {...field}
            onSelection={(value) => setTacEmployment(value?.toLowerCase())}
          ></FWOIteractiveTemplateRow>
        )
      } else {
        return null
      }
    }

    if (field.replacement === 'TAC_U18') {
      if (employeeU18 === 'yes') {
        return (
          <FWOIteractiveTemplateRow
            key={index}
            {...field}
            onSelection={(value) => setTacU18(value?.toLowerCase())}
          ></FWOIteractiveTemplateRow>
        )
      } else {
        return null
      }
    }

    if (
      (field.replacement === 'GUARDIAN_FIRSTNAME' ||
        field.replacement === 'GUARDIAN_LASTNAME' ||
        field.replacement === 'TAC_U18') &&
      employeeU18 !== 'yes'
    ) {
      return null
    }

    if (field.replacement === 'AWARD_CONDITION') {
      if (
        employeeU18 === 'no' &&
        (tacEmployment === 'award' || tacEmployment === 'enterprise agreement')
      ) {
        return (
          <FWOIteractiveTemplateRow
            key={index}
            {...field}
          ></FWOIteractiveTemplateRow>
        )
      } else {
        return null
      }
    }

    if (field.replacement === 'AWARD_U18') {
      if (
        employeeU18 === 'yes' &&
        (tacU18 === 'award' || tacU18 === 'enterprise agreement')
      ) {
        return (
          <FWOIteractiveTemplateRow
            key={index}
            {...field}
          ></FWOIteractiveTemplateRow>
        )
      } else {
        return null
      }
    }

    return (
      <FWOIteractiveTemplateRow
        key={index}
        {...field}
      ></FWOIteractiveTemplateRow>
    )
  }

  const validatePage = async (validateForm, setState) => {
    return validateForm().then((errors) => {
      const state = Object.keys(errors).length === 0
      setState(state)
      return state
    })
  }

  const renderPage = (
    page: IInteractiveTemplatePage,
    index: number,
    validateForm
  ) => {
    if (page?.name === Page.EmploymentDetails) {
      return (
        <Accordion
          heading={page.name}
          key={index}
          expanded={showEmploymentDetailsPage}
          onClick={() => {
            if (currentPage !== Page.EmploymentDetails) {
              setCurrentPage(Page.EmploymentDetails)
            }
            setShowEmploymentDetailsPage(!showEmploymentDetailsPage)
          }}
        >
          <Conditional if={showEmploymentDetailsPage}>
            {page.sections?.map((section, index) =>
              renderSection(section, index)
            )}
            <p className="form-submit">
              <Button
                className="ui is-primary button form-submit"
                onClick={() =>
                  validatePage(validateForm, setValidEmploymentDetails)
                }
              >
                Next
              </Button>
            </p>
          </Conditional>
        </Accordion>
      )
    }
    if (page?.name === Page.LeaveDetails) {
      return (
        <Accordion
          heading={page.name}
          key={index}
          expanded={showLeaveDetailsPage}
          onClick={() => {
            validatePage(validateForm, setValidEmploymentDetails).then(
              (isPrevPageValid: boolean) => {
                if (showLeaveDetailsPage) {
                  // fold page
                  setShowLeaveDetailsPage(false)
                } else {
                  if (isPrevPageValid) {
                    setCurrentPage(Page.LeaveDetails)
                    setShowLeaveDetailsPage(true)
                    window.scrollTo(0, 0)
                  } else {
                    // keep folded
                    setShowLeaveDetailsPage(false)
                  }
                }
              }
            )
          }}
        >
          <Conditional if={validEmploymentDetails}>
            {page.sections?.map((section, index) =>
              renderSection(section, index)
            )}
            <p className="form-submit">
              <Button
                className="ui is-primary button"
                onClick={() =>
                  validateForm().then((errors) => console.log(errors))
                }
              >
                Next
              </Button>
            </p>
          </Conditional>
        </Accordion>
      )
    }
  }

  const renderSection = (
    section: IInteractiveTemplateSection,
    index: number
  ) => {
    if (section?.fields) {
      return (
        <section key={index}>
          <Conditional if={section.name != 'Leave details'}>
            <h3>{section.name}</h3>
          </Conditional>
          {section.fields.map((field, index) => renderField(field, index))}
        </section>
      )
    } else {
      return <></>
    }
  }

  const onSubmit = (values) => {
    values['POSTAL_ADDRESS'] = buildAddressFromValues('POSTAL_ADDRESS', values)
    values['BUSINESS_ADDRESS'] = buildAddressFromValues(
      'BUSINESS_ADDRESS',
      values
    )

    setInitialValues({ ...initialValues, ...values })
    props.handleNext(values, FormKey.AnnualLeaveAgreement)
    if (currentPage === Page.EmploymentDetails) {
      setValidEmploymentDetails(true)
      setCurrentPage(Page.LeaveDetails)
    } else if (currentPage === Page.LeaveDetails) {
      setShowLeaveDetailsPage(false)
    }
  }

  return (
    <>
      <PageTitle title="Agreement to take annual Leave in advance - Templates - Fair Work Ombudsman" />

      <Formik
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={initialValues}
        validateOnMount={false}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, validateForm }) => (
          <Form
            className="ui form osp-variant large"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="accordian">
              {props.template?.pages?.map((page, index) =>
                renderPage(page, index, validateForm)
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}
